import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Follow a language change into the participant's record.
 *
 * The choice was only ever kept in localStorage, so it changed the screen and
 * nothing else: every later ticket, reminder and one-time code still went out
 * in whatever language the person happened to register in. The server already
 * renders mail from participants.locale — it simply was never told when that
 * changed.
 *
 * Shared by the app and the microsite. Both frontends set the locale at
 * registration and on the OAuth start URL, so this covers the one case neither
 * did: somebody who signs up in Greek, then switches to English later.
 *
 * Pass the frontend's own client — each builds its own instance around a
 * different token store (Capacitor Preferences in the app, localStorage on the
 * web), so there is no single `api` for this to import.
 */
export function useLocaleSync(api) {
  const { i18n } = useTranslation();
  const synced = useRef(null);

  useEffect(() => {
    const locale = i18n.resolvedLanguage;
    if (!locale) return undefined;

    // The first run is the language they already have, not a change.
    if (synced.current === null) {
      synced.current = locale;
      return undefined;
    }
    if (synced.current === locale) return undefined;
    synced.current = locale;

    let cancelled = false;
    (async () => {
      try {
        const { access } = (await api.readTokens()) || {};
        // Anonymous browsing is the normal case on the microsite; there is no
        // record to update and no session to prompt for.
        if (!access || cancelled) return;
        await api.participants.updateMe({ locale });
      } catch (err) {
        // A preference, not a session: never let this surface to the user or
        // take a signed-in session down with it.
        console.error('[locale] sync failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage, api]);
}

export default useLocaleSync;
